import type { UserRole } from '@sfa/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { EmptyState, LoadingBlock } from '@/components/ui';
import { useAuth } from '@/store/auth';

/**
 * Every route is its own chunk, loaded only when visited.
 *
 * Before this, App.tsx statically imported all fourteen pages — Dashboard's
 * own `import { ... } from 'recharts'` (~113KB gzipped) rode along in that
 * one eager bundle for every role on every load, including a Retailer who
 * never sees a chart at all. Splitting per-route removes all of it, and
 * recharts specifically, from the entry chunk: it is now fetched only when
 * a StaffDashboard (Admin/Sales Executive) actually renders.
 *
 * Named exports, not default — React.lazy() only resolves a default export,
 * hence the `.then(m => ({ default: m.X }))` adapter on each.
 */
const LoginPage = React.lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const ChangePasswordPage = React.lazy(() =>
  import('@/pages/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })));
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const ProductsPage = React.lazy(() => import('@/pages/ProductsPage').then((m) => ({ default: m.ProductsPage })));
const RetailersPage = React.lazy(() => import('@/pages/RetailersPage').then((m) => ({ default: m.RetailersPage })));
const SalesExecutivesPage = React.lazy(() =>
  import('@/pages/SalesExecutivesPage').then((m) => ({ default: m.SalesExecutivesPage })));
const MastersPage = React.lazy(() => import('@/pages/MastersPage').then((m) => ({ default: m.MastersPage })));
const IncentivesPage = React.lazy(() => import('@/pages/IncentivesPage').then((m) => ({ default: m.IncentivesPage })));
const TargetsPage = React.lazy(() => import('@/pages/TargetsPage').then((m) => ({ default: m.TargetsPage })));
const AttendancePage = React.lazy(() => import('@/pages/AttendancePage').then((m) => ({ default: m.AttendancePage })));
const MyBeatTodayPage = React.lazy(() =>
  import('@/pages/MyBeatTodayPage').then((m) => ({ default: m.MyBeatTodayPage })));
const OrdersPage = React.lazy(() => import('@/pages/OrdersPage').then((m) => ({ default: m.OrdersPage })));
const NewOrderPage = React.lazy(() => import('@/pages/NewOrderPage').then((m) => ({ default: m.NewOrderPage })));
const OrderDetailPage = React.lazy(() =>
  import('@/pages/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })));
const ReportsPage = React.lazy(() => import('@/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const OrderSummaryPage = React.lazy(() =>
  import('@/pages/OrderSummaryPage').then((m) => ({ default: m.OrderSummaryPage })));
const PaymentCollectionSummaryPage = React.lazy(() =>
  import('@/pages/PaymentCollectionSummaryPage').then((m) => ({ default: m.PaymentCollectionSummaryPage })));
const VehiclesPage = React.lazy(() => import('@/pages/VehiclesPage').then((m) => ({ default: m.VehiclesPage })));
const VehiclePlansPage = React.lazy(() =>
  import('@/pages/VehiclePlansPage').then((m) => ({ default: m.VehiclePlansPage })));
const VehiclePlanCreatePage = React.lazy(() =>
  import('@/pages/VehiclePlanCreatePage').then((m) => ({ default: m.VehiclePlanCreatePage })));
const VehiclePlanDetailPage = React.lazy(() =>
  import('@/pages/VehiclePlanDetailPage').then((m) => ({ default: m.VehiclePlanDetailPage })));
const VehiclePlanSummaryPage = React.lazy(() =>
  import('@/pages/VehiclePlanSummaryPage').then((m) => ({ default: m.VehiclePlanSummaryPage })));
const DriversPage = React.lazy(() => import('@/pages/DriversPage').then((m) => ({ default: m.DriversPage })));
const CompanySettingsPage = React.lazy(() => import('@/pages/CompanySettingsPage').then((m) => ({ default: m.CompanySettingsPage })));
const GodownsPage = React.lazy(() => import('@/pages/GodownsPage').then((m) => ({ default: m.GodownsPage })));
const DriverDashboardPage = React.lazy(() =>
  import('@/pages/DriverDashboardPage').then((m) => ({ default: m.DriverDashboardPage })));
const DriverPlanDetailPage = React.lazy(() =>
  import('@/pages/DriverPlanDetailPage').then((m) => ({ default: m.DriverPlanDetailPage })));

/**
 * React Query configuration.
 *
 * refetchOnWindowFocus is the mechanism behind "everything updates in real
 * time": returning to the tab re-validates whatever is on screen, so a
 * dashboard left open does not quietly show yesterday's numbers. Genuinely
 * live events (order status changes) arrive over SSE and invalidate the
 * relevant queries directly.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry an auth or permission failure — the answer will not
        // change, and retrying just delays the redirect to the login screen.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;

        return failureCount < 2;
      },
    },
  },
});

function RequireAuth({ children, roles }: { children: React.ReactNode; roles?: UserRole[] }) {
  const status = useAuth((state) => state.status);
  const user = useAuth((state) => state.user);
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center">
        <LoadingBlock label="Restoring your session…" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    // Remember where they were headed so the login can send them back.
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  /*
   * An administrator-set password is single-use — the backend refuses every
   * protected endpoint except POST /auth/change-password until it has been
   * replaced (see index.php's front-controller gate). Redirecting here,
   * before AppShell ever mounts, matters as much as the redirect itself:
   * AppShell fires its own protected queries (My Beat Today, the realtime
   * connection) on mount, and every one of those would come back 403 while
   * this flag is set. A manually typed URL for any RequireAuth route lands
   * here too, so there is no way around the requirement but the page itself.
   */
  if (user?.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return (
      <AppShell>
        <EmptyState
          title="Access denied"
          description="Your role does not have permission to view this section."
        />
      </AppShell>
    );
  }

  // The shell (header, sidebar, BeatTopBar) renders synchronously — only the
  // routed page itself is a lazy chunk, so switching pages never blanks the
  // chrome around it, and the very first paint after login is never a blank
  // page waiting on a chunk to arrive over a slow field connection.
  return (
    <AppShell>
      <React.Suspense fallback={<LoadingBlock label="Loading…" />}>{children}</React.Suspense>
    </AppShell>
  );
}

export function AppRoutes() {
  const restore = useAuth((state) => state.restore);

  // Exchange the HttpOnly refresh cookie for an access token on boot, so a
  // hard refresh does not bounce a signed-in user to the login screen.
  React.useEffect(() => {
    void restore();
  }, [restore]);

  // Outer boundary: covers the one route with no AppShell around it
  // (/login) and acts as a fallback for anything that somehow suspends
  // above the inner, shell-preserving boundary in RequireAuth.
  return (
    <React.Suspense
      fallback={<div className="grid min-h-screen place-items-center"><LoadingBlock label="Loading…" /></div>}
    >
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/*
        Deliberately not wrapped in RequireAuth/AppShell: AppShell mounts
        protected queries on its own (My Beat Today, realtime) that the
        backend refuses while must_change_password is set, so this page has
        to be reachable without ever mounting that chrome. Its own guards
        (ChangePasswordPage) handle the loading/unauthenticated/not-required
        cases that RequireAuth would otherwise cover.
      */}
      <Route path="/change-password" element={<ChangePasswordPage />} />

      <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />

      {/*
        Vehicle Planning Phase 5D — Driver Portal. DRIVER only, matching
        DriverPortalController's own role gate exactly. Nothing above this
        in the file grants DRIVER access to any other route — see the
        explicit roles added to /products and /orders* below, which
        previously had none and so were technically reachable (never
        actually usable — the API itself returns 403 — but reachable at the
        route level) by any authenticated role including DRIVER.
      */}
      <Route
        path="/driver"
        element={
          <RequireAuth roles={['DRIVER']}>
            <DriverDashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/my-deliveries/:id"
        element={
          <RequireAuth roles={['DRIVER']}>
            <DriverPlanDetailPage />
          </RequireAuth>
        }
      />

      {/*
        Explicit roles added in Phase 5D — the pre-existing three roles
        (SUPER_ADMIN/SALES_EXECUTIVE/RETAILER, exactly AppShell's own `ALL`
        nav constant) are unchanged; DRIVER is the only role newly excluded
        here, since it never used to exist as a possible value at all.
      */}
      <Route
        path="/products"
        element={
          <RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE', 'RETAILER']}>
            <ProductsPage />
          </RequireAuth>
        }
      />

      <Route
        path="/retailers"
        element={
          <RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE']}>
            <RetailersPage />
          </RequireAuth>
        }
      />

      <Route
        path="/sales-executives"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <SalesExecutivesPage />
          </RequireAuth>
        }
      />

      <Route
        path="/masters/:resource"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <MastersPage />
          </RequireAuth>
        }
      />

      <Route
        path="/incentives"
        element={
          <RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE']}>
            <IncentivesPage />
          </RequireAuth>
        }
      />

      <Route
        path="/targets"
        element={
          <RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE']}>
            <TargetsPage />
          </RequireAuth>
        }
      />

      <Route
        path="/attendance"
        element={
          <RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE']}>
            <AttendancePage />
          </RequireAuth>
        }
      />

      <Route
        path="/my-beat"
        element={
          <RequireAuth roles={['SALES_EXECUTIVE']}>
            <MyBeatTodayPage />
          </RequireAuth>
        }
      />

      <Route
        path="/orders"
        element={<RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE', 'RETAILER']}><OrdersPage /></RequireAuth>}
      />
      <Route
        path="/orders/new"
        element={<RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE', 'RETAILER']}><NewOrderPage /></RequireAuth>}
      />
      <Route
        path="/orders/:id"
        element={<RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE', 'RETAILER']}><OrderDetailPage /></RequireAuth>}
      />

      <Route
        path="/reports"
        element={
          <RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE']}>
            <ReportsPage />
          </RequireAuth>
        }
      />

      <Route
        path="/order-summary"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <OrderSummaryPage />
          </RequireAuth>
        }
      />

      <Route
        path="/payment-summary"
        element={
          <RequireAuth roles={['SUPER_ADMIN', 'SALES_EXECUTIVE']}>
            <PaymentCollectionSummaryPage />
          </RequireAuth>
        }
      />

      {/*
        SUPER_ADMIN only for every verb including read (see
        VehicleController's own docblock) — narrower than the Masters
        convention (roles=STAFF), matching the API's own role gate exactly.
      */}
      <Route
        path="/vehicles"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <VehiclesPage />
          </RequireAuth>
        }
      />

      {/*
        Vehicle Planning Phase 5A — Driver Master. SUPER_ADMIN only, every
        verb, matching DriverController's own role gate exactly. Adding the
        DRIVER role to UserRole does not grant it access here — this array
        simply never lists DRIVER, the same way it never lists RETAILER.
      */}
      <Route
        path="/drivers"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <DriversPage />
          </RequireAuth>
        }
      />

      {/*
        Vehicle Planning Phase 5E — Godown / Dispatch Location Master.
        SUPER_ADMIN only, every verb, matching GodownController's own role
        gate exactly.
      */}
      {/* Company settings — the seller identity printed on documents. SUPER_ADMIN only, matching CompanyController. */}
      <Route
        path="/settings/company"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <CompanySettingsPage />
          </RequireAuth>
        }
      />

      <Route
        path="/godowns"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <GodownsPage />
          </RequireAuth>
        }
      />

      {/*
        SUPER_ADMIN only, every verb — matches VehiclePlanController's own
        role gate exactly. Phase 3 stops at PLANNED; there is no dispatch
        route, page, or action anywhere in this section.
      */}
      <Route
        path="/vehicle-plans/new"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <VehiclePlanCreatePage />
          </RequireAuth>
        }
      />
      <Route
        path="/vehicle-plans/:id"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <VehiclePlanDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/vehicle-plans"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <VehiclePlansPage />
          </RequireAuth>
        }
      />

      {/*
        Read-only reporting/export feature, entirely additive — see
        VehiclePlanSummaryController's own doc comment. SUPER_ADMIN only,
        same role gate as every other /vehicle-plans route above.
      */}
      <Route
        path="/vehicle-plan-summary"
        element={
          <RequireAuth roles={['SUPER_ADMIN']}>
            <VehiclePlanSummaryPage />
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </React.Suspense>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/*
        Last line of defence: an uncaught render error anywhere below this —
        a widget, a page, AppShell itself — used to unmount all the way to
        <div id="root"> with nothing left on screen. This catches it and
        offers Retry instead of a blank tab. Toaster stays outside so a
        toast already in flight is not swallowed by the same error.
      */}
      <ErrorBoundary>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ErrorBoundary>
      {/* Toasts are the only feedback channel for a save that fails after the
          dialog has closed, so they mount once at the root. */}
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  );
}
